<script setup lang="ts">
import type { InputPrimaryEvent, NativeSelectOption } from '@shared/ui/fields'
import type { InteractionEditorProps } from '../../../../model/assessments/interaction-editors.hook'

import { faArrowDown, faArrowUp, faTrashCan } from '@fortawesome/pro-regular-svg-icons'

import { AppText } from '@shared/ui/typography'
import { InputPrimary, NativeSelect } from '@shared/ui/fields'
import { BtnCheckbox, BtnIcon, BtnSecondary } from '@shared/ui/buttons'

import { useInteractionMultipleChoice } from '../../../../model/assessments/interaction-editors.hook'

defineOptions({
  name: 'InteractionMultipleChoice',
})

const props = defineProps<InteractionEditorProps>()

const {
  refMode,
  refModeOptions,
  refModeHint,
  refChoices,
  refCanRemoveChoice,
  setMode,
  addChoice,
  removeChoice,
  moveChoiceUp,
  moveChoiceDown,
  setChoiceText,
  toggleChoiceCorrect,
} = useInteractionMultipleChoice({
  screenId: () => props.screenId,
  activityId: () => props.activityId,
  interaction: () => props.interaction,
})
</script>

<template>
  <div class="interaction-multiple-choice">
    <!-- The mode is visible next to the correct-toggles on purpose: under `single` the store
         applies radio semantics (marking one option correct clears the others), and a toggle
         that unticks its neighbours is only surprising when the mode is hidden. -->
    <NativeSelect
      name="mode"
      label="Answer mode"
      :options="refModeOptions"
      :value="refMode"
      @change="(option: NativeSelectOption) => setMode(String(option.value))"
    />
    <AppText
      name="body2"
      color="slate-500"
    >
      {{ refModeHint }}
    </AppText>

    <AppText name="body2-medium">
      Options
    </AppText>

    <ul class="interaction-multiple-choice__items">
      <li
        v-for="choice in refChoices"
        :key="choice.id"
        class="interaction-multiple-choice__item"
      >
        <!-- One line per option: whether it is the right answer, the answer itself, and the
             three controls. No "Option N" heading and no field label — the row's position in
             the list is its number, and the placeholder says what the box is for. -->
        <BtnCheckbox
          text="Correct"
          class="interaction-multiple-choice__correct"
          :checked="choice.correct"
          @click="toggleChoiceCorrect(choice.id, choice.correct)"
        />

        <InputPrimary
          name="choice-text"
          class-name="interaction-multiple-choice__text"
          :value="choice.text"
          placeholder="What the kid can choose"
          @input="(event: InputPrimaryEvent) => setChoiceText(choice.id, String(event.value))"
        />

        <div class="interaction-multiple-choice__actions">
          <BtnIcon
            :icon="faArrowUp"
            size="small"
            title="Move option up"
            :disabled="choice.isFirstInOrder"
            @click="moveChoiceUp(choice.id)"
          />
          <BtnIcon
            :icon="faArrowDown"
            size="small"
            title="Move option down"
            :disabled="choice.isLastInOrder"
            @click="moveChoiceDown(choice.id)"
          />
          <BtnIcon
            :icon="faTrashCan"
            size="small"
            color="red"
            title="Remove option"
            :disabled="!refCanRemoveChoice"
            @click="removeChoice(choice.id)"
          />
        </div>
      </li>
    </ul>

    <AppText
      v-if="!refCanRemoveChoice"
      name="body2"
      color="slate-500"
    >
      A multiple choice interaction needs at least two options.
    </AppText>

    <BtnSecondary
      text="Add option"
      @click="addChoice"
    />
  </div>
</template>

<style scoped lang="scss" src="./InteractionMultipleChoice.scss" />
